import { UrlFilterParams } from "@/enums/library";
import URLManager from "@/services/URLManager";
import { computed, onMounted, ref, watch, type Ref } from "vue";
import { useRoute, useRouter } from "vue-router";

type PaginationOptions = {
    page?: number;
    pageSize?: number;
};

const tracked = {
    page: { key: UrlFilterParams.Page, value: ["1"] },
    pageSize: { key: UrlFilterParams.PageSize, value: ["10"] }
};

export function usePagination<T>(options?: PaginationOptions) {
    const route = useRoute();
    const router = useRouter();

    const page = ref(1);
    const pageSize = ref(10);
    const trackInUrl = ref(true);

    const data: Ref<T[]> = ref([]);

    let urlManager: URLManager | null = null;

    const paginatedData = computed<T[][]>(() => {
        if (!data.value.length) return [];

        const byPage: T[][] = [];

        for (let i = 0; i < data.value.length; i += pageSize.value) {
            byPage.push(data.value.slice(i, i + pageSize.value));
        }
        return byPage;
    });

    const currentPageItems = computed(() => paginatedData.value[page.value - 1] ?? []);

    function initializePagination() {
        const pageQuery = parseInt(route.query.p as string, 10);
        const pageSizeQuery = parseInt(route.query.ps as string, 10);

        pageSize.value = !Number.isNaN(pageSizeQuery) ? pageSizeQuery : (options?.pageSize ?? 10);
        page.value = !Number.isNaN(pageQuery) && pageQuery > 1 ? pageQuery : (options?.page ?? 1);
    }

    function setPage(newPage: number) {
        page.value = newPage;
    }

    function setPageSize(newPageSize: number) {
        pageSize.value = newPageSize;
    }

    function setPaginationData(newData: T[]) {
        data.value = newData;
    }

    onMounted(() => {
        initializePagination();

        if (trackInUrl.value) {
            urlManager = new URLManager(router, {
                defaults: [tracked.page, tracked.pageSize],
                tracked: [tracked.page.key, tracked.pageSize.key]
            });
        }
    });

    watch(page, (newValue, oldValue) => {
        if (trackInUrl.value && urlManager && newValue !== oldValue) {
            urlManager.setURLParams([{ ...tracked.page, value: [String(newValue)] }]).catch(console.error);
        }
    });

    watch(pageSize, (newValue, oldValue) => {
        if (trackInUrl.value && urlManager && newValue !== oldValue) {
            urlManager.setURLParams([{ ...tracked.pageSize, value: [String(newValue)] }]).catch(console.error);
        }
    });

    return {
        page,
        pageSize,
        trackInUrl,
        setPage,
        setPageSize,
        currentPageItems,
        setPaginationData
    };
}
